import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Handshake } from "@mui/icons-material";
import CustomDataGrid from '../../../../../components/ui/customDataGrid/customDataGrid.component';
import { CONTRACT_TERMS_ENDPOINT } from '../../../../../consts';
import { useHasPermission } from '../../../../../contexts/auth';
import { ContractTermList } from '../../../../../types/contracts';
import { formatNumber } from '../../../../../utils';
import EntityReferenceLink from '../../../../../components/ui/entityReference/entityReferenceLink.component';

export const EquipmentContracts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewContracts = useHasPermission("settlement.view_contractterm");
  const canViewContract = useHasPermission("settlement.view_contract");

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "contract__number",
      headerName: t("Contract Number"),
      valueGetter: (value, row) => row.contract?.number || "-",
      renderCell: (params) => (
        <EntityReferenceLink
          entityType="contract"
          entityId={params.row.contract?.id}
          label={params.row.contract?.number || "-"}
          canPreview={canViewContract}
        />
      ),
    },
    {
      field: "contract__start_date",
      headerName: t("Contract Start Date"),
      valueGetter: (value, row) => row.contract?.start_date || "-",
    },
    {
      field: "contract__end_date",
      headerName: t("Contract End Date"),
      valueGetter: (value, row) => row.contract?.end_date || "-",
    },
    {
      field: "kind",
      headerName: t("Kind"),
      valueGetter: (value, row) => row.kind_display,
    },
    {
      field: "settlement_cycle",
      headerName: t("Settlement Cycle"),
      valueGetter: (value, row) => row.settlement_cycle_display,
    },
    {
      field: "rent_price",
      headerName: t("Rent Price"),
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
  ], [t, canViewContract]);

  const handleRowClick = (id: string, row?: ContractTermList) => {
    navigate(`/contracts/${row?.contract?.id}`);
  };

  if (!canViewContracts) {
    return null;
  }

  const contractsUrl = id ? `${CONTRACT_TERMS_ENDPOINT}?equipment=${id}` : CONTRACT_TERMS_ENDPOINT;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Handshake />}
        title={t('Assigned Contracts')}
        url={contractsUrl}
        gridSettingsKey="EQUIPMENT_CONTRACTS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="equipmentContractAdded"
      />
    </Box>
  );
};
